import { useEffect, useState, useCallback } from 'react';
import { useSignalR } from '../../context/realtime/SignalRContext';

export function useSignalRIdSet(trackerName, startEvent, endEvent) {
  const { connection, connectionStatus } = useSignalR();
  const [ids, setIds] = useState(new Set());

  // live updates (declared first so handlers are attached before the snapshot is requested)
  useEffect(() => {
    if (!connection || connectionStatus !== 'Connected') return undefined;

    const handleStart = (incomingIds) => {
      setIds((prev) => new Set([...prev, ...incomingIds]));
    };
    const handleEnd = (incomingIds) => {
      setIds((prev) => {
        const next = new Set(prev);
        incomingIds.forEach((id) => next.delete(id));
        return next;
      });
    };

    connection.on(startEvent, handleStart);
    connection.on(endEvent, handleEnd);

    return () => {
      connection.off(startEvent, handleStart);
      connection.off(endEvent, handleEnd);
    };
  }, [connection, connectionStatus, startEvent, endEvent]);

  //resync: replace the set with the server's current state on connect/reconnect (and tab focus)
  useEffect(() => {
    if (!connection || connectionStatus !== 'Connected') return undefined;

    let cancelled = false;
    const resync = async () => {
      try {
        const current = await connection.invoke('GetCurrentStatus', trackerName);
        if (!cancelled) setIds(new Set(current)); // replaces, so finished invoices drop out
      } catch (err) {
        console.error('Resync failed:', err);
      }
    };

    resync();
    window.addEventListener('focus', resync);

    return () => {
      cancelled = true;
      window.removeEventListener('focus', resync);
    };
  }, [connection, connectionStatus, trackerName]);

  
  const optimisticAdd = useCallback((idsToAdd) => {
    setIds((prev) => new Set([...prev, ...(Array.isArray(idsToAdd) ? idsToAdd : [idsToAdd])]));
  }, []);

  const optimisticRemove = useCallback((idsToRemove) => {
    setIds((prev) => {
      const next = new Set(prev);
      (Array.isArray(idsToRemove) ? idsToRemove : [idsToRemove]).forEach((id) => next.delete(id));
      return next;
    });
  }, []);

  return { ids, optimisticAdd, optimisticRemove };
}
